/**
 * Emails a VMAX shop sends its customers and followers through pokefans (db 0057): the shop writes
 * and picks a group, pokefans picks the people (only those who accepted marketing emails and didn't
 * turn that shop off) and sends from its own address "en nombre de @tienda". The shop sees counts.
 */
import { isSegmentKey, NO_FILTERS, parseSegmentFilters, type SegmentFilters, type SegmentKey } from "./marketCustomers";

/** Groups about what people look for or whether they follow: results under MIN_GROUP_SHOWN stay hidden, and no narrowing. */
export const BLIND_SEGMENTS: readonly string[] = ["followers", "wants_my_stock"];

export const SHOP_EMAIL_SUBJECT_MAX = 80;
export const SHOP_EMAIL_BODY_MAX = 2000;
export const SHOP_EMAIL_LISTINGS = 6;
/** One email per shop and person every SHOP_EMAIL_GAP_DAYS; at most SHOP_EMAIL_WEEKLY per person in 7 days, all shops. */
export const SHOP_EMAIL_GAP_DAYS = 7;
export const SHOP_EMAIL_WEEKLY = 4;
/** Orders a campaign gets credit for: from its recipients, with the shop, within these days of the email. */
export const SHOP_EMAIL_CREDIT_DAYS = 7;

export const CAMPAIGN_STATUSES = ["queued", "sending", "sent", "rejected"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export interface ShopCampaignInput {
  segment: SegmentKey;
  filters: SegmentFilters;
  subject: string;
  body: string;
  couponId: number | null;
  listingIds: number[];
}

/** Links only to pokefans: anything that looks like another site or an email address is refused. */
export function foreignLink(text: string): boolean {
  const urls = text.match(/\b(?:https?:\/\/|www\.)[^\s]+/gi) ?? [];
  const bare = text.match(/\b[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|net|org|ar|io|me|link|ly|gg|app|shop|store|xyz)(?:\/[^\s]*)?\b/gi) ?? [];
  const email = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(text);
  // Contacts go through the order chat once there's an order, never in a marketing email.
  const phone = /\+?\d[\d\s-]{7,}\d/.test(text);
  return email || phone || [...urls, ...bare].some((u) => !/^(?:https?:\/\/)?(?:www\.)?pokefans\.fun(?:\/|$)/i.test(u));
}

export function parseShopCampaign(b: Record<string, unknown>): { ok: true; value: ShopCampaignInput } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  const segment = b.segment;
  if (!isSegmentKey(segment)) errors.push("segment");
  const raw = (b.filters && typeof b.filters === "object" ? b.filters : {}) as Record<string, unknown>;
  const filters = parseSegmentFilters((k) => (raw[k] === undefined || raw[k] === null ? null : String(raw[k])));
  if (!filters) errors.push("filters");
  // Narrowing a blind group could single someone out.
  else if (BLIND_SEGMENTS.includes(String(segment)) && JSON.stringify(filters) !== JSON.stringify(NO_FILTERS)) errors.push("filters");
  // One line, no control characters (nothing can reach the email's headers).
  const subject = typeof b.subject === "string" ? b.subject.replace(/[\r\n\t]+/g, " ").replace(/\s+/g, " ").trim() : "";
  if (subject.length < 3 || subject.length > SHOP_EMAIL_SUBJECT_MAX) errors.push("subject");
  const body = typeof b.body === "string" ? b.body.replace(/\r\n?/g, "\n").trim() : "";
  if (!body || body.length > SHOP_EMAIL_BODY_MAX) errors.push("body");
  if (foreignLink(`${subject}\n${body}`)) errors.push("links");
  const couponId = b.couponId == null || b.couponId === "" ? null : Number(b.couponId);
  if (couponId !== null && (!Number.isInteger(couponId) || couponId <= 0)) errors.push("couponId");
  const listingIds = Array.isArray(b.listingIds) ? [...new Set(b.listingIds.map(Number))] : [];
  if (listingIds.length > SHOP_EMAIL_LISTINGS || listingIds.some((n) => !Number.isInteger(n) || n <= 0)) errors.push("listingIds");
  if (errors.length) return { ok: false, errors: [...new Set(errors)] };
  return { ok: true, value: { segment: segment as SegmentKey, filters: filters!, subject, body, couponId, listingIds } };
}

/** A campaign as its shop sees it: what went out and what came of it (counts only). */
export interface ShopCampaign {
  id: number;
  kind: "manual" | "auto_thanks" | "auto_winback" | "auto_news";
  segment: string;
  subject: string;
  body: string;
  couponCode: string | null;
  listingIds: number[];
  status: CampaignStatus;
  /** The shop's first campaign waits for pokefans' approval. */
  awaitingApproval: boolean;
  rejectReason: string | null;
  /** null: a blind group (followers, wants) under MIN_GROUP_SHOWN, where numbers could point at someone. */
  audience: number | null;
  sent: number | null;
  /** People who opened a listing from it, and orders from its recipients with the shop within SHOP_EMAIL_CREDIT_DAYS. */
  clicks: number | null;
  orders: number | null;
  createdAt: string;
  sentAt: string | null;
}

export interface ShopEmailsView {
  campaigns: ShopCampaign[];
  /** Manual campaigns left this month (Argentina) under the plan. */
  left: number;
  perMonth: number;
  /** Sending is switched off site-wide for now: campaigns wait in the queue. */
  paused: boolean;
  /** The shop's emails were turned off (a spam report or the admin), with the reason. */
  blocked: string | null;
}

export const AUTOMATIONS = ["thanks", "winback", "news"] as const;
export type AutomationKind = (typeof AUTOMATIONS)[number];
/** Thanks: a personal coupon a week after the order completes. Come back: after WINBACK_DAYS without buying, once every WINBACK_REPEAT_DAYS. News: weekly, with NEWS_MIN new listings. */
export const THANKS_AFTER_DAYS = 7;
export const WINBACK_DAYS = 60;
export const WINBACK_REPEAT_DAYS = 180;
export const NEWS_MIN = 5;

export interface Automation {
  kind: AutomationKind;
  enabled: boolean;
  /** thanks: the personal coupon (percent or amount, valid days); winback: an optional shop coupon. */
  config: { kind?: "percent" | "amount"; value?: number; days?: number; couponId?: number | null };
}
